import type { RefObject, PropsWithChildren } from 'react'
import { createContext, useRef } from 'react'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { createCtx } from 'src/utils/contextState'

/**
 * 'default': All parameters are set by user
 * 'reuse': Retrain with the same dataset as previous one
 * If dataset has changed, should set the mode back to 'default'
 */
export type TrainLoraDatasetMode = 'default' | 'reuse'

export const [TrainLoraContext, TrainLoraContextProvider] = createCtx<{
  // mediaIds: string[]
  title: string
  triggerWords: string
  /**
   * Whether this train is based on imported
   */
  importedLoraList: string[]
  /**
   * Which model will the Lora be trained with
   */
  versionId?: string
  modelType?: GenerationModelType
  /**
   * Which base model this training version will be added to
   */
  primaryLoraModelId?: string
  /**
   * LoRA category
   */
  category?: string
}>({
  // mediaIds: [],
  title: '',
  triggerWords: '',
  importedLoraList: [],
  versionId: undefined,
  modelType: GenerationModelType.SdxlModel,
  primaryLoraModelId: undefined,
  category: undefined,
})

export const [DatasetDialogListContext, DatasetDialogListContextProvider] =
  createCtx<string[]>([])

/**
 * Performance Optimization
 * We don't need UI to be reactive with dataset item mapping
 * Map<datasetId, mediaId[]>
 */
export const DatasetDialogItemsRefContext = createContext<
  RefObject<Map<string, string[]> | null>
>({
  current: null,
})
export function DatasetDialogItemsRefContextProvider(props: PropsWithChildren) {
  const ref = useRef(new Map<string, string[]>())
  return (
    <DatasetDialogItemsRefContext.Provider value={ref}>
      {props.children}
    </DatasetDialogItemsRefContext.Provider>
  )
}

/**
 * For tracking where each media comes from
 * Map<mediaId, from>
 */
export const ImageItemsSourceContext = createContext<
  RefObject<Map<string, 'task' | 'upload' | 'dataset'> | null>
>({
  current: null,
})
export function ImageItemsSourceContextProvider(props: PropsWithChildren) {
  const ref = useRef(new Map<string, 'task' | 'upload' | 'dataset'>())
  return (
    <ImageItemsSourceContext.Provider value={ref}>
      {props.children}
    </ImageItemsSourceContext.Provider>
  )
}

export const [TrainLoraSubmitContext, TrainLoraSubmitContextProvider] =
  createCtx<{ isSubmitting: boolean }>({ isSubmitting: false })

export const [TrainLoraDatasetContext, TrainLoraDatasetContextProvider] =
  createCtx<{
    datasetMode: TrainLoraDatasetMode
    /**
     * ID of a previous training task
     * Should be set when datasetMode is 'reuse'
     */
    datasetId?: string
  }>({
    datasetMode: 'default',
    datasetId: undefined,
  })
